import React, { useState, useEffect, useRef } from 'react';
import contactUsImage from '../images/contact_us.webp';
import contactPersonImage from '../images/contact_us_person.png';
import FormSuccessPopup from './FormSuccessPopup';
import FormLoader from './FormLoader';

const ContactUs = () => {
  const heroRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: '',
    mobile: '',
    email: '',
    location: '',
    category: '',
    course: '',
    customCourse: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState('success');
  const [popupMessage, setPopupMessage] = useState('');

  useEffect(() => {
    // Scroll to top on mount
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Disable scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-section');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    document.querySelectorAll('.fade-section').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const courseCategories = {
    'trending-courses': {
      name: 'Trending Courses',
      courses: [
        'Artificial Intelligence & Gen-AI',
        'Prompt Engineering',
        'Cloud Computing',
        'DevOps & SRE',
        'Software Testing',
        'Data Analytics',
        'Data Engineering',
        'Full-Stack Development',
        'Power Platform / Low-Code Automation',
        'Data Science',
        'UI/UX Design'
      ]
    },
    'ai-section': {
      name: 'Artificial Intelligence',
      courses: [
        'Data Science with Gen-AI Training',
        'Generative AI Training',
        'Prompt Engineering Training',
        'Agentic AI Training',
        'Machine Learning Training',
        'Microsoft Copilot Training',
        'AI in Project Management Training',
        'AI in Excel Training',
        'AI Business Analyst Training',
        'AI Chatbot Development Training'
      ]
    },
    'software-testing': {
      name: 'Software Testing',
      courses: [
        'Selenium Automation Training (Java/Python/C#)',
        'Cypress Automation Training (JavaScript)',
        'Playwright Automation Training (TypeScript)',
        'API Testing Training (Postman + RestAssured)',
        'Performance Testing Training (JMeter)',
        'Mobile Automation Training (Appium)'
      ]
    },
    'cloud-computing': {
      name: 'Cloud Computing',
      courses: [
        'AWS Training',
        'Azure Training',
        'GCP Training',
        'Cloud Certification Training'
      ]
    },
    'devops': {
      name: 'DevOps',
      courses: [
        'DevOps Training',
        'Linux Training',
        'Git & GitHub Training',
        'CI/CD Jenkins Training',
        'Docker Training',
        'Kubernetes Training',
        'Terraform Training',
        'Ansible Training',
        'DevSecOps Training',
        'Monitoring & Observability Training'
      ]
    },
    'web-development': {
      name: 'Web Development',
      courses: [
        'React Training',
        'MERN Full-Stack Training',
        'Java Full-Stack Training',
        'Python Full-Stack Training',
        'DOT NET Full-Stack Training'
      ]
    },
    'programming': {
      name: 'Programming',
      courses: [
        'Java Programming',
        'Python Programming',
        'JavaScript Programming',
        'C Programming',
        'C++ Programming',
        'Advanced Python Training',
        'Python with Django Training',
        'Python with Flask Training'
      ]
    },
    'data-engineering': {
      name: 'Data Engineering',
      courses: [
        'Data Engineering Training',
        'Big Data Training',
        'Cloud Data Engineering Training'
      ]
    },
    'testing-addons': {
      name: 'Testing Add-ons',
      courses: [
        'ISTQB Certification Training',
        'SQL for Testers',
        'API Security Testing',
        'Automation Framework Design Training'
      ]
    },
    'power-platform': {
      name: 'Power Platform',
      courses: [
        'Power Apps Training',
        'Power Automate Training',
        'Power BI Training',
        'Microsoft Copilot Studio Training'
      ]
    },
    'data-analytics': {
      name: 'Data Analytics',
      courses: [
        'Data Analytics Training',
        'Power BI Training',
        'Tableau Training',
        'Excel Advanced Analytics Training'
      ]
    },
    'ui-ux': {
      name: 'UI/UX Design',
      courses: [
        'UI/UX Design Training',
        'Figma Training',
        'Adobe XD Training',
        'Responsive Web Design Training'
      ]
    },
    'others': {
      name: 'Others',
      courses: ['Others']
    }
  };

  const handleCategoryChange = (e) => {
    setFormData({ ...formData, category: e.target.value, course: '', customCourse: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mobile number
    if (formData.mobile.length !== 10) {
      alert('Please enter a valid 10-digit mobile number');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    const submissionData = {
      ...formData,
      course: formData.category === 'others' ? formData.customCourse : formData.course
    };
    console.log('Contact Form Submitted', submissionData);

    // Minimum 3 second loading animation
    const startTime = Date.now();

    if (process.env.NODE_ENV === 'development') {
      await new Promise(resolve => setTimeout(resolve, 3000));
      setPopupType('success');
      setPopupMessage('Submitted Successfully');
      setShowPopup(true);
      setFormData({
        fullName: '',
        mobile: '',
        email: '',
        location: '',
        category: '',
        course: '',
        customCourse: '',
        message: ''
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/register.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Result:', result);

      // Ensure minimum 3 seconds have passed
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
      }

      if (result.success) {
        setPopupType('success');
        setPopupMessage('Submitted Successfully');
        setShowPopup(true);
        setFormData({
          fullName: '',
          mobile: '',
          email: '',
          location: '',
          category: '',
          course: '',
          customCourse: '',
          message: ''
        });
      } else {
        setPopupType('error');
        setPopupMessage('Submission Failed');
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
      }
      setPopupType('error');
      setPopupMessage('Submission Failed');
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={heroRef} style={{ backgroundColor: '#ffffff' }}>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in-section {
          animation: fadeInUp 1s ease-out forwards;
        }

        .fade-section {
          opacity: 0;
        }

        @keyframes floatImage {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes morphBubble {
          0%, 100% {
            border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
            transform: rotate(0deg);
          }
          25% {
            border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
            transform: rotate(90deg);
          }
          50% {
            border-radius: 50% 60% 30% 60% / 30% 60% 70% 40%;
            transform: rotate(180deg);
          }
          75% {
            border-radius: 60% 40% 60% 30% / 70% 30% 60% 50%;
            transform: rotate(270deg);
          }
        }

        .floating-image {
          animation: floatImage 3s ease-in-out infinite;
        }

        .morphing-bubble {
          animation: morphBubble 8s ease-in-out infinite;
        }

        @media (max-width: 1200px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .we-are-here-section > div {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .branches-section > div > div {
            grid-template-columns: 1fr !important;
            max-width: 500px !important;
          }
        }

        @media (max-width: 768px) {
          .contact-hero {
            padding: 1.5rem !important;
            height: auto !important;
            min-height: 85vh !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }
          .contact-hero > div:last-child {
            padding: 11rem 0 2rem !important;
            text-align: left !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: flex-start !important;
            height: auto !important;
            min-height: auto !important;
          }
          .contact-hero h1 {
            font-size: 2rem !important;
            line-height: 1.3 !important;
            margin-bottom: 1rem !important;
          }
          .contact-hero p {
            font-size: 0.95rem !important;
            text-align: left !important;
          }
          .contact-section {
            padding: 2rem 1rem !important;
          }
          .contact-grid > div:first-child {
            max-width: 100% !important;
            padding: 0 0.5rem !important;
          }
          .contact-grid > div:first-child h2 {
            text-align: center !important;
            font-size: 1.8rem !important;
          }
          .contact-grid {
            justify-items: center !important;
          }
          .contact-grid > div:last-child {
            width: 100% !important;
            max-width: 420px !important;
            margin: 0 auto !important;
          }
          .we-are-here-section {
            padding: 2rem 1.5rem !important;
          }
          .branches-section {
            padding: 2rem 1.5rem !important;
          }
        }

        @media (max-width: 480px) {
          .contact-hero {
            padding: 1rem !important;
            height: auto !important;
            min-height: 80vh !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }
          .contact-hero > div:last-child {
            padding: 10rem 0 1.5rem !important;
            min-height: auto !important;
          }
          .contact-hero h1 {
            font-size: 1.6rem !important;
            margin-bottom: 0.8rem !important;
          }
        }
      `}</style>

      <section 
        className="contact-hero"
        style={{
          position: 'relative',
          padding: '6rem 2rem 5rem',
          textAlign: 'center',
          overflow: 'hidden',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          marginTop:'-170px',
          justifyContent: 'center'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${contactUsImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 1,
          willChange: 'transform'
        }}></div>

        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          zIndex: 2
        }}></div>

        <div style={{ 
          position: 'relative', 
          zIndex: 3, 
          maxWidth: '1200px', 
          margin: '0 auto',
          color: 'white',
          padding:'7.5rem 0 5rem 0'
        }}>
          <h1 style={{ 
            fontSize: 'clamp(2.5rem, 5vw, 4rem)', 
            marginBottom: '1.5rem', 
            fontWeight: '800',
            textShadow: '2px 4px 8px rgba(0,0,0,0.5)',
            lineHeight: '1.2'
          }}>
            Talk to our <span style={{ color: '#ff6b35' }}>Expert Consultant</span>
          </h1>
          <p style={{ 
            fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', 
            lineHeight: '1.8', 
            maxWidth: '800px', 
            margin: '0 auto',
            opacity: 0.95,
            textShadow: '1px 2px 4px rgba(0,0,0,0.5)'
          }}>
            Get personalized career guidance and find the perfect course for your IT journey. Our expert consultants are here to help you succeed.
          </p>
        </div>
      </section>

      <section className="contact-section fade-section" style={{ 
        padding: '2rem 2rem', 
        background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)'
      }}>
        <div className="contact-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1.1fr 0.7fr',
          gap: '3rem',
          alignItems: 'center',
          maxWidth: '1100px',
          margin: '0 auto'
        }}>
          <div style={{ 
            padding: '0.5rem 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            maxWidth: '550px'
          }}>
            <h2 style={{ 
              color: '#001f3f', 
              fontSize: 'clamp(2.2rem, 4.5vw, 3rem)',
              fontWeight: '700',
              lineHeight: '1.3',
              marginBottom: '0.5rem'
            }}>
              Let's Start Your IT<br/><span style={{ color: '#ff6b35' }}>Journey Together</span>
            </h2>
            
            <p style={{
              color: '#4b5563',
              fontSize: 'clamp(1.05rem, 2vw, 1.2rem)',
              lineHeight: '1.6',
              marginBottom: '0.8rem',
              fontWeight: '500'
            }}>
              Have questions about our courses, batch timings, or placement support? Our expert counselors are here to guide you every step of the way.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{ 
                  color: '#ff6b35', 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  marginTop: '-2px'
                }}>✓</span>
                <p style={{ 
                  color: '#374151', 
                  fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                  lineHeight: '1.5',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Get personalized course recommendations based on your career goals
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{ 
                  color: '#ff6b35', 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  marginTop: '-2px'
                }}>✓</span>
                <p style={{ 
                  color: '#374151', 
                  fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                  lineHeight: '1.5',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Learn about flexible batch timings and training modes
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{ 
                  color: '#ff6b35', 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  marginTop: '-2px'
                }}>✓</span>
                <p style={{ 
                  color: '#374151', 
                  fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                  lineHeight: '1.5',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Discover exclusive offers and group discounts
                </p>
              </div>
            </div>
          </div>

          <div style={{
            position: 'relative',
            zIndex: 1,
            width: '420px',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '24px',
            padding: '1.8rem',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(226, 232, 240, 0.8)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
              padding: '1.2rem 1.5rem',
              borderRadius: '24px 24px 0 0',
              margin: '-1.8rem -1.8rem 1rem',
              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.3)'
            }}>
              <p style={{ 
                fontSize: '1.8rem',
                fontWeight: '800',
                marginBottom: '0.5rem',
                textAlign: 'center',
                color: '#ffffff'
              }}>Get in Touch</p>
              <p style={{
                color: '#cbd5e1',
                fontSize: '0.9rem',
                marginBottom: 0,
                textAlign: 'center'
              }}>Fill out the form and we'll get back to you shortly</p>
            </div>

            {submitStatus && (
              <div style={{
                padding: '0.6rem',
                marginBottom: '0.6rem',
                borderRadius: '8px',
                background: submitStatus.type === 'success' ? '#d4edda' : '#f8d7da',
                color: submitStatus.type === 'success' ? '#155724' : '#721c24',
                border: `1px solid ${submitStatus.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                fontSize: '0.8rem',
                textAlign: 'center'
              }}>
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              <input
                type="text"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                onKeyPress={(e) => {
                  if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault();
                }}
                required
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.9rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              />

              <input
                type="tel"
                placeholder="Mobile Number"
                value={formData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, mobile: value });
                }}
                pattern="[0-9]{10}"
                title="Please enter exactly 10 digits"
                required
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.9rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              />

              <input
                type="email"
                placeholder="Email ID"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                required
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.9rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              />

              <select
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.9rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <option value="" disabled>Preferred Location</option>
                <option value="Chrompet">Chrompet</option>
                <option value="Velachery">Velachery</option>
                <option value="Online">Online</option>
              </select>

              <select
                value={formData.category}
                onChange={handleCategoryChange}
                required
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                  backdropFilter: 'blur(15px)',
                  fontSize: '0.9rem',
                  color: '#1a365d',
                  outline: 'none',
                  boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select Course Category</option>
                {Object.entries(courseCategories).map(([key, category]) => (
                  <option key={key} value={key}>{category.name}</option>
                ))}
              </select>

              {formData.category === 'others' ? (
                <input 
                  type="text" 
                  placeholder="Please specify your course" 
                  value={formData.customCourse} 
                  onChange={(e) => setFormData({ ...formData, customCourse: e.target.value })} 
                  required
                  style={{
                    padding: '0.4rem 0.6rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)',
                    backdropFilter: 'blur(15px)',
                    fontSize: '0.9rem',
                    color: '#1a365d',
                    outline: 'none',
                    boxShadow: 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }} 
                />
              ) : (
                <select
                  value={formData.course}
                  onChange={(e) => setFormData({ ...formData, course: e.target.value, customCourse: '' })}
                  disabled={!formData.category}
                  required
                  style={{
                    padding: '0.4rem 0.6rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: formData.category
                      ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.9) 50%, rgba(255, 255, 255, 0.85) 100%)'
                      : 'linear-gradient(145deg, rgba(200, 200, 200, 0.7) 0%, rgba(220, 220, 220, 0.6) 50%, rgba(200, 200, 200, 0.5) 100%)',
                    backdropFilter: 'blur(15px)',
                    fontSize: '0.9rem',
                    color: formData.category ? '#1a365d' : '#64748b',
                    outline: 'none',
                    boxShadow: formData.category
                      ? 'inset 2px 2px 6px rgba(0, 0, 0, 0.1), inset -2px -2px 6px rgba(255, 255, 255, 0.8), 0 4px 12px rgba(0, 49, 93, 0.15)'
                      : 'inset 1px 1px 3px rgba(0, 0, 0, 0.05), inset -1px -1px 3px rgba(255, 255, 255, 0.4)',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    cursor: formData.category ? 'pointer' : 'not-allowed',
                    opacity: formData.category ? 1 : 0.7
                  }}
                >
                  <option value="">Select Specific Course</option>
                  {formData.category && courseCategories[formData.category].courses.map((course, index) => (
                    <option key={index} value={course}>{course}</option>
                  ))}
                </select>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: 'linear-gradient(135deg, #00315d 0%, #004080 50%, #0066cc 100%)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.2)',
                  padding: '0.7rem 1.5rem',
                  borderRadius: '20px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)',
                  marginTop: '0.5rem',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  opacity: isSubmitting ? 0.7 : 1
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* We Are Here Section */}
      <section className="fade-section" style={{ 
        padding: '2rem 1rem', 
        background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ color: 'white' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              textAlign: 'center',
              color: 'white'
            }}>
              We Are <span style={{ color: '#ff6b35' }}>Here</span>
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{
                  color: '#ff6b35',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginTop: '-2px',
                  flexShrink: 0
                }}>✓</span>
                <p style={{
                  color: '#e0f2fe',
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  lineHeight: '1.7',
                  margin: 0
                }}>
                  To guide you through your IT career journey with expert mentorship and industry insights
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{
                  color: '#ff6b35',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginTop: '-2px',
                  flexShrink: 0
                }}>✓</span>
                <p style={{
                  color: '#e0f2fe',
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  lineHeight: '1.7',
                  margin: 0
                }}>
                  To provide comprehensive hands-on training with real-world projects and practical scenarios
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{
                  color: '#ff6b35',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginTop: '-2px',
                  flexShrink: 0
                }}>✓</span>
                <p style={{
                  color: '#e0f2fe',
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  lineHeight: '1.7',
                  margin: 0
                }}>
                  To support your placement goals with dedicated assistance and interview preparation
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{
                  color: '#ff6b35',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginTop: '-2px',
                  flexShrink: 0
                }}>✓</span>
                <p style={{
                  color: '#e0f2fe',
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  lineHeight: '1.7',
                  margin: 0
                }}>
                  To offer flexible learning schedules including weekend and weekday batches that fit your lifestyle
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <span style={{
                  color: '#ff6b35',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginTop: '-2px',
                  flexShrink: 0
                }}>✓</span>
                <p style={{
                  color: '#e0f2fe',
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  lineHeight: '1.7',
                  margin: 0
                }}>
                  To answer all your queries and help you make informed decisions about your career path
                </p>
              </div>
            </div>
          </div>

          <div style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '350px'
          }}>
            <div 
              className="morphing-bubble" 
              style={{
                position: 'absolute',
                width: '350px',
                height: '350px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(96, 165, 250, 0.3) 100%)',
                filter: 'blur(40px)',
                zIndex: 1
              }}
            />
            <img 
              src={contactPersonImage}
              alt="IT training consultant at Oranium Tech Chennai" 
              className="floating-image"
              style={{
                position: 'relative',
                width: '75%',
                maxWidth: '380px',
                height: 'auto',
                zIndex: 2,
                filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))'
              }}
            />
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section className="fade-section" style={{ 
        padding: '3rem 1rem', 
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            color: '#001f3f',
            fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)',
            fontWeight: '700',
            marginBottom: '2rem'
          }}>
            Reach Out to Our Branch Directly !!
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem',
            alignItems: 'stretch',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {/* Velachery Branch */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.2rem',
              boxShadow: '0 20px 60px rgba(0, 31, 63, 0.15), 0 10px 30px rgba(0, 31, 63, 0.1)',
              border: '1px solid rgba(0, 49, 93, 0.08)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              transform: 'translateY(0) translateZ(0)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
                borderRadius: '12px',
                padding: '0.8rem 1rem',
                marginBottom: '1rem',
                boxShadow: '0 8px 20px rgba(0, 31, 63, 0.3)'
              }}>
                <h3 style={{
                  color: 'white',
                  fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                  fontWeight: '700',
                  margin: 0,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}>Velachery Branch</h3>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>📍</span>
                    <strong style={{
                      color: '#001f3f',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      margin: 0
                    }}>Address:</strong>
                  </div>
                  <p style={{
                    color: '#4b5563',
                    fontSize: '0.9rem',
                    lineHeight: '1.6',
                    margin: 0,
                    paddingLeft: '2.1rem',
                    minHeight: '4.8rem'
                  }}>
                    52, Ponniamman Complex, 2nd Floor, Velachery Main Rd, nearby Dhandeeswaram Bus Stop, Velachery, Chennai, Tamil Nadu 600042
                  </p>
                </div>
                
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>📧</span>
                    <strong style={{
                      color: '#001f3f',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      margin: 0
                    }}>Email:</strong>
                  </div>
                  <a 
                    href="mailto:oraniumtech@gmail.com"
                    style={{
                      color: '#ff6b35',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      textDecoration: 'none',
                      paddingLeft: '2.1rem',
                      display: 'block',
                      transition: 'color 0.3s'
                    }}
                  >
                    oraniumtech@gmail.com
                  </a>
                </div>
                
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>📞</span>
                    <strong style={{
                      color: '#001f3f',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      margin: 0
                    }}>Phone:</strong>
                  </div>
                  <a 
                    href="tel:+917871719161"
                    style={{
                      color: '#ff6b35',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      textDecoration: 'none',
                      paddingLeft: '2.1rem',
                      display: 'block',
                      transition: 'color 0.3s'
                    }}
                  >
                    7871719161
                  </a>
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.830830496074!2d80.21911627454672!3d12.982669614636563!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a525db92a754b87%3A0xf52babbb2731460e!2sOranium%20Tech%20-%20Full%20Stack%20Developer%20Course%20in%20Chennai%20%7C%20Cloud%20Computing%20course%20%7C%20Software%20Testing%20Training!5e0!3m2!1sen!2sin!4v1766408343303!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: '2px solid #001f3f', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Velachery Branch Location"
                />
              </div>
            </div>

            {/* Chrompet Branch */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.2rem',
              boxShadow: '0 20px 60px rgba(0, 31, 63, 0.15), 0 10px 30px rgba(0, 31, 63, 0.1)',
              border: '1px solid rgba(0, 49, 93, 0.08)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              transform: 'translateY(0) translateZ(0)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
                borderRadius: '12px',
                padding: '0.8rem 1rem',
                marginBottom: '1rem',
                boxShadow: '0 8px 20px rgba(0, 31, 63, 0.3)'
              }}>
                <h3 style={{
                  color: 'white',
                  fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                  fontWeight: '700',
                  margin: 0,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}>Chrompet Branch</h3>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '0.4rem'
                  }}>
                    <span style={{
                      fontSize: '1.3rem',
                      color: '#ff6b35',
                      flexShrink: 0
                    }}>📍</span>
                    <strong style={{
                      color: '#001f3f',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      margin: 0
                    }}>Address:</strong>
                  </div>
                  <p style={{
                    color: '#4b5563',
                    fontSize: '0.9rem',
                    lineHeight: '1.6',
                    margin: 0,
                    paddingLeft: '2.1rem',
                    minHeight: '4.8rem'
                  }}>
                    9, 2nd Floor, 14th cross street,<br/>
                    New Colony, Chrompet,<br/>
                    Chennai 600044
                  </p>
                </div>
                
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>📧</span>
                    <strong style={{
                      color: '#001f3f',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      margin: 0
                    }}>Email:</strong>
                  </div>
                  <a 
                    href="mailto:oraniumtech@gmail.com"
                    style={{
                      color: '#ff6b35',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      textDecoration: 'none',
                      paddingLeft: '2.1rem',
                      display: 'block',
                      transition: 'color 0.3s'
                    }}
                  >
                    oraniumtech@gmail.com
                  </a>
                </div>
                
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>📞</span>
                    <strong style={{
                      color: '#001f3f',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      margin: 0
                    }}>Phone:</strong>
                  </div>
                  <a 
                    href="tel:+917871719161"
                    style={{
                      color: '#ff6b35',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      textDecoration: 'none',
                      paddingLeft: '2.1rem',
                      display: 'block',
                      transition: 'color 0.3s'
                    }}
                  >
                    7871719161
                  </a>
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.37541243773!2d80.1338436745462!3d12.947812815400399!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a525f457fdc7327%3A0x51cc2694c2752e13!2sOranium%20Tech%20Chromepet%20-%20Full%20Stack%20Developer%20Course%20in%20Chennai%20%7C%20Cloud%20Computing%20course%20%7C%20Software%20Testing%20Training!5e0!3m2!1sen!2sus!4v1766408262305!5m2!1sen!2sus"
                  width="100%"
                  height="280"
                  style={{ border: '2px solid #001f3f', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Chrompet Branch Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <FormSuccessPopup 
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        type={popupType}
        message={popupMessage}
      />
      <FormLoader isLoading={isSubmitting} />
    </div>
  );
};

export default ContactUs;
